"use client";

import { useEffect, useState, type ReactNode } from "react";
import dynamic from "next/dynamic";

// Lazy-load deck.gl + maplibre on the client only. They pull in WebGL,
// canvas APIs, and ~250 kB of JS we don't want in the initial bundle or
// the SSR bundle.
const Map3D = dynamic(() => import("./CommunityPrismMap3D"), {
  ssr: false,
});

type Props = {
  /** Server-rendered static map shown until the interactive view mounts. */
  children: ReactNode;
};

// Probe for WebGL support before mounting deck.gl. On WebGL-blocked
// browsers (older devices, hardened-mode browsers, GPU disabled) deck.gl
// throws during init and the canvas never paints — without this guard
// the SSR SVG would be unmounted and replaced by a blank pane.
function detectWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    return Boolean(gl);
  } catch {
    return false;
  }
}

// Skip the WebGL map on coarse-pointer devices. deck.gl always handles
// single-finger pan inside the canvas, which traps vertical page scroll
// on phones — the user can't scroll past the figure without aiming around
// it. The hover-anchored tooltip also doesn't reach a stable state on
// touch. The SSR SVG stays interactive enough (per-prism `<title>`
// elements) and lets the page scroll normally.
//
// Whitespace-aligned with the Tailwind variant
// `[@media(pointer:coarse)]:` used in by-community/page.tsx so a
// codebase grep finds both call sites.
const COARSE_POINTER_QUERY = "(pointer:coarse)";
function detectCoarsePointer(): boolean {
  try {
    return window.matchMedia(COARSE_POINTER_QUERY).matches;
  } catch {
    return false;
  }
}

export function CommunityMapHost({ children }: Props) {
  const [interactive, setInteractive] = useState(false);

  // Defer the swap one frame past hydration so the static SVG paints
  // first — gives a stable initial impression and avoids a layout flash
  // on slow devices while deck.gl initialises. WebGL-blocked and
  // coarse-pointer browsers never flip the flag, so the SSR SVG remains
  // the rendered figure.
  useEffect(() => {
    if (!detectWebGL()) return;
    if (detectCoarsePointer()) return;
    const id = requestAnimationFrame(() => setInteractive(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!interactive) return <>{children}</>;
  return <Map3D />;
}
